import React from 'react';

/*
 * Catalog messages with inline markup (ENG-386). Plain `t()` returns a
 * string, so a sentence with a bold amount or a `<code>` path in the middle
 * would have to be split into fragments — which translators cannot reorder.
 * Instead the catalog keeps the whole sentence with a `{{placeholder}}` for
 * the marked-up part, the component calls `t(key)` *without* that parameter
 * so the placeholder survives interpolation, and `richMessage` substitutes a
 * React node for it here. Any `{{placeholder}}` not in `parts` is rendered
 * literally, and the pseudo-locale smoke tests (`hardCodedStrings` in
 * `src/test/pseudoLocaleGuards.js`) report a leaked `{{…}}` as an offender
 * regardless of the locale, so a renamed or misspelt part cannot pass.
 */

/** `{{name}}` with the name captured, so `split` interleaves text and names. */
const PLACEHOLDER = /\{\{\s*([^{}\s]+)\s*\}\}/;

/**
 * Text and React nodes for `message`, with each `{{name}}` replaced by
 * `parts[name]`. Returns an array suitable as JSX children.
 *
 * `text(chunk, index)` optionally wraps each non-empty run of plain text —
 * for layouts that style the words around a number differently ("3" large,
 * "of 12 badges unlocked" small) without splitting the message in two.
 */
export const richMessage = (message, parts = {}, { text = (chunk) => chunk } = {}) =>
  String(message ?? '')
    .split(PLACEHOLDER)
    .map((chunk, index) => {
      if (index % 2 === 0) return chunk === '' ? chunk : <React.Fragment key={index}>{text(chunk, index)}</React.Fragment>;
      return <React.Fragment key={index}>{chunk in parts ? parts[chunk] : `{{${chunk}}}`}</React.Fragment>;
    });

/** Component form of `richMessage` for use directly in JSX. */
export const RichMessage = ({ message, parts, text }) => <>{richMessage(message, parts, text ? { text } : undefined)}</>;

/**
 * `richMessage` with every named part wrapped in `<strong>` — the common case
 * of an emphasised amount, name or path inside a sentence.
 */
export const withStrong = (message, values = {}, options) =>
  richMessage(
    message,
    Object.fromEntries(Object.entries(values).map(([name, value]) => [name, <strong>{value}</strong>])),
    options
  );
